// Input validation and sanitization utilities

export const validateEmail = (email: string): { isValid: boolean; error?: string } => {
  if (!email) {
    return { isValid: false, error: 'Email is required' };
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Invalid email format' };
  }
  
  if (email.length > 254) {
    return { isValid: false, error: 'Email too long' };
  }
  
  return { isValid: true };
};

export const validatePassword = (password: string): { isValid: boolean; error?: string; strength?: string } => {
  if (!password) {
    return { isValid: false, error: 'Password is required' };
  }
  
  if (password.length < 8) {
    return { isValid: false, error: 'Password must be at least 8 characters' };
  }
  
  if (password.length > 128) {
    return { isValid: false, error: 'Password too long' };
  }
  
  // Check password strength
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  
  const strengthScore = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChar].filter(Boolean).length;
  
  let strength = 'weak';
  if (strengthScore >= 3) strength = 'medium';
  if (strengthScore === 4 && password.length >= 12) strength = 'strong';
  
  return { isValid: true, strength };
};

export const validateName = (name: string, fieldName: string = 'Name'): { isValid: boolean; error?: string } => {
  if (!name) {
    return { isValid: false, error: `${fieldName} is required` };
  }
  
  const trimmedName = name.trim();
  if (trimmedName.length < 1) {
    return { isValid: false, error: `${fieldName} cannot be empty` };
  }
  
  if (trimmedName.length > 100) {
    return { isValid: false, error: `${fieldName} too long (max 100 characters)` };
  }
  
  // Allow only letters, spaces, hyphens, and apostrophes
  const nameRegex = /^[a-zA-Z\s\-']+$/;
  if (!nameRegex.test(trimmedName)) {
    return { isValid: false, error: `${fieldName} contains invalid characters` };
  }
  
  return { isValid: true };
};

export const validatePhone = (phone: string): { isValid: boolean; error?: string } => {
  if (!phone) {
    return { isValid: false, error: 'Phone number is required' };
  }
  
  // Remove all non-digit characters for validation
  const digitsOnly = phone.replace(/\D/g, '');
  
  if (digitsOnly.length < 10 || digitsOnly.length > 15) {
    return { isValid: false, error: 'Invalid phone number format' };
  }
  
  return { isValid: true };
};

export const sanitizeText = (text: string): string => {
  if (!text) return '';
  
  return text
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Remove script tags specifically (double protection)
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove potentially dangerous characters
    .replace(/[<>]/g, '')
    // Normalize whitespace
    .replace(/\s+/g, ' ')
    .trim();
};

export const validateTextLength = (text: string, maxLength: number, fieldName: string = 'Text'): { isValid: boolean; error?: string } => {
  if (text && text.length > maxLength) {
    return { isValid: false, error: `${fieldName} too long (max ${maxLength} characters)` };
  }
  
  return { isValid: true };
};

export const validateUrl = (url: string, fieldName: string = 'URL'): { isValid: boolean; error?: string } => {
  if (!url) {
    return { isValid: true }; // URLs are often optional
  }
  
  try {
    new URL(url);
    return { isValid: true };
  } catch {
    return { isValid: false, error: `Invalid ${fieldName} format` };
  }
};

export interface ParsedResumeData {
  [key: string]: any;
  fullName?: string;
  name?: string;
  jobTitle?: string;
  title?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  phoneNumber?: string;
  contactNumber?: string;
  telephone?: string;
  city?: string;
  location?: string;
  linkedinUrl?: string;
  portfolioUrl?: string;
  professionalSummary?: string;
  profileSummary?: string;
  objective?: string;
  summary?: string;
  workExperience?: any[];
  experience?: any[];
  professionalExperience?: any[];
  employmentHistory?: any[];
  workHistory?: any[];
  education?: any[];
  academicBackground?: any[];
  educationHistory?: any[];
  qualifications?: any[];
  skills?: { [key: string]: any; technical?: string[]; soft?: string[] } | string[];
  technicalSkills?: any;
  softSkills?: any;
  coreCompetencies?: any;
  competencies?: any;
  languages?: any[];
  certifications?: any[];
  personalInfo?: any;
  contactInfo?: any;
  contact?: any;
  basics?: any;
  resumeData?: any;
  parsedData?: any;
}

const firstText = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
    if (Array.isArray(value)) {
      const nested = firstText(...value);
      if (nested) return nested;
    }
    if (value && typeof value === 'object') {
      const objectValue = value as Record<string, unknown>;
      const nested = firstText(objectValue.text, objectValue.value, objectValue.name, objectValue.label);
      if (nested) return nested;
    }
  }
  return '';
};

const toTextArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .flatMap(item => toTextArray(item))
      .filter(Boolean);
  }

  if (typeof value === 'string' && value.trim()) {
    return value
      .split(/\n|•|- /)
      .map(item => item.trim())
      .filter(Boolean);
  }

  return [];
};

const toFlexibleTextArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap(item => toFlexibleTextArray(item)).filter(Boolean);
  }

  if (typeof value === 'string' && value.trim()) {
    return value
      .split(/\n|\r|\u2022|\u00e2\u20ac\u00a2|\u00c3\u00a2\u00e2\u201a\u00ac\u00c2\u00a2|- /)
      .map(item => item.trim())
      .filter(Boolean);
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return [String(value)];
  }

  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>)
      .flatMap(item => toFlexibleTextArray(item))
      .filter(Boolean);
  }

  return [];
};

const toObjectArray = (...values: unknown[]): any[] => {
  for (const value of values) {
    if (Array.isArray(value)) {
      return value.filter(Boolean);
    }

    if (value && typeof value === 'object') {
      const objectValues = Object.values(value as Record<string, unknown>).filter(Boolean);
      if (objectValues.some(item => item && typeof item === 'object')) {
        return objectValues;
      }
      if (objectValues.some(item => firstText(item).length > 0)) {
        return [value];
      }
    }
  }

  return [];
};

const uniqueTextArray = (items: string[]) => Array.from(new Set(
  items.map(item => item.trim()).filter(Boolean)
));

export const normalizeParsedResumeData = (data: ParsedResumeData = {}): ParsedResumeData => {
  const root = (data.parsedData || data.resumeData || data) as ParsedResumeData;
  const personalInfo = root.personalInfo || {};
  const contactInfo = root.contactInfo || root.contact || root.basics || {};

  const rawWorkExperience = toObjectArray(
    root.workExperience,
    root.experience,
    root.professionalExperience,
    root.employmentHistory,
    root.workHistory,
    root.careerHistory,
    root.jobs,
  );
  const workExperience = rawWorkExperience
    .map((exp: any) => ({
        ...exp,
        company: firstText(exp.company, exp.companyName, exp.employer, exp.organization),
        companyName: firstText(exp.companyName, exp.company, exp.employer, exp.organization),
        jobTitle: firstText(exp.jobTitle, exp.position, exp.title, exp.role),
        position: firstText(exp.position, exp.jobTitle, exp.title, exp.role),
        responsibilities: firstText(exp.responsibilities, exp.description, exp.duties, exp.achievements),
        description: toFlexibleTextArray(exp.description || exp.responsibilities || exp.duties || exp.achievements),
        duration: firstText(exp.duration, [exp.startYear, exp.endYear].filter(Boolean).join(' - '), [exp.startDate, exp.endDate].filter(Boolean).join(' - ')),
      }))
    .filter(exp => Object.values(exp).some(value => firstText(value)));

  const rawEducation = toObjectArray(
    root.education,
    root.academicBackground,
    root.educationHistory,
    root.qualifications,
    root.academics,
  );
  const education = rawEducation
    .map((edu: any) => ({
        ...edu,
        schoolName: firstText(edu.schoolName, edu.institution, edu.school, edu.university, edu.college),
        institution: firstText(edu.institution, edu.schoolName, edu.school, edu.university, edu.college),
        degree: firstText(edu.degree, edu.qualification, edu.program, edu.course),
        dates: firstText(edu.dates, [edu.startYear, edu.endYear].filter(Boolean).join(' - '), [edu.startDate, edu.endDate].filter(Boolean).join(' - ')),
      }))
    .filter(edu => Object.values(edu).some(value => firstText(value)));

  const rawSkills = root.skills;
  const skills = Array.isArray(rawSkills)
    ? { technical: uniqueTextArray(rawSkills.flatMap(skill => toFlexibleTextArray(skill))), soft: [] }
    : {
        technical: uniqueTextArray([
          ...toFlexibleTextArray(rawSkills),
          ...toFlexibleTextArray(rawSkills?.technical),
          ...toFlexibleTextArray(rawSkills?.technicalSkills),
          ...toFlexibleTextArray(rawSkills?.hardSkills),
          ...toFlexibleTextArray(rawSkills?.tools),
          ...toFlexibleTextArray(rawSkills?.software),
          ...toFlexibleTextArray(rawSkills?.programmingLanguages),
          ...toFlexibleTextArray(root.technicalSkills),
          ...toFlexibleTextArray(root.coreCompetencies),
          ...toFlexibleTextArray(root.competencies),
        ]),
        soft: uniqueTextArray([
          ...toFlexibleTextArray(rawSkills?.soft),
          ...toFlexibleTextArray(rawSkills?.softSkills),
          ...toFlexibleTextArray(root.softSkills),
        ]),
      };

  return {
    ...root,
    fullName: firstText(root.fullName, root.name, personalInfo.fullName, personalInfo.name, contactInfo.fullName, contactInfo.name, [personalInfo.firstName, personalInfo.lastName].filter(Boolean).join(' ')),
    jobTitle: firstText(root.jobTitle, root.title, root.currentTitle, personalInfo.jobTitle, personalInfo.title, contactInfo.jobTitle, contactInfo.title),
    email: firstText(root.email, personalInfo.email, contactInfo.email),
    phone: firstText(root.phone, root.mobile, root.phoneNumber, root.contactNumber, root.telephone, personalInfo.phone, personalInfo.mobile, personalInfo.phoneNumber, contactInfo.phone, contactInfo.mobile, contactInfo.phoneNumber, contactInfo.telephone),
    city: firstText(root.city, root.location, personalInfo.city, personalInfo.location, contactInfo.city, contactInfo.location),
    linkedinUrl: firstText(root.linkedinUrl, root.linkedin, personalInfo.linkedin, personalInfo.linkedinUrl, contactInfo.linkedin, contactInfo.linkedinUrl),
    portfolioUrl: firstText(root.portfolioUrl, root.portfolio, personalInfo.portfolio, personalInfo.portfolioUrl, contactInfo.portfolio, contactInfo.portfolioUrl),
    summary: firstText(root.summary, root.professionalSummary, root.profileSummary, root.objective),
    workExperience,
    education,
    skills,
    languages: toFlexibleTextArray(root.languages),
    certifications: toFlexibleTextArray(root.certifications || root.certificates),
  };
};

export const validateParsedResumeData = (data: ParsedResumeData): { isValid: boolean; missingFields: string[]; errors: string[]; hasWorkExperience: boolean; hasEducation: boolean } => {
  const normalizedData = normalizeParsedResumeData(data);
  const missingFields: string[] = [];
  const errors: string[] = [];

  // Check required fields with smarter validation
  const hasFullName = normalizedData.fullName && normalizedData.fullName.trim().length > 2;
  const hasEmail = normalizedData.email && normalizedData.email.trim().length > 5 && normalizedData.email.includes('@');
  const hasPhone = normalizedData.phone && normalizedData.phone.trim().length > 6;
  
  // Check work experience - look for meaningful content
  let hasWorkExperience = false;
  if (normalizedData.workExperience && normalizedData.workExperience.length > 0) {
    hasWorkExperience = normalizedData.workExperience.some(exp => {
      const meaningfulValues = [
        exp.company,
        exp.companyName,
        exp.employer,
        exp.organization,
        exp.jobTitle,
        exp.position,
        exp.title,
        exp.role,
        exp.duration,
        exp.responsibilities,
        ...(Array.isArray(exp.description) ? exp.description : [exp.description]),
      ].filter(value => firstText(value).length > 2);

      return meaningfulValues.length >= 2 || firstText(exp.responsibilities, exp.description).length > 20;
    });
  }

  // Check education - look for meaningful content
  let hasEducation = false;
  if (normalizedData.education && normalizedData.education.length > 0) {
    hasEducation = normalizedData.education.some(edu => {
      const meaningfulValues = [
        edu.schoolName,
        edu.institution,
        edu.school,
        edu.university,
        edu.college,
        edu.degree,
        edu.qualification,
        edu.program,
        edu.course,
        edu.dates,
      ].filter(value => firstText(value).length > 2);

      return meaningfulValues.length >= 1;
    });
  }

  const technicalSkills = Array.isArray((normalizedData.skills as any)?.technical) ? (normalizedData.skills as any).technical : [];
  const softSkills = Array.isArray((normalizedData.skills as any)?.soft) ? (normalizedData.skills as any).soft : [];
  const hasSkills = technicalSkills.length > 0 || softSkills.length > 0;
  const hasSummary = Boolean(normalizedData.summary && normalizedData.summary.trim().length > 30);
  const hasIdentity = Boolean(hasFullName || hasEmail || hasPhone || normalizedData.jobTitle);
  const resumeSectionCount = [hasWorkExperience, hasEducation, hasSkills, hasSummary].filter(Boolean).length;
  const hasResumeStructure = Boolean(
    (hasIdentity && resumeSectionCount >= 1) ||
    resumeSectionCount >= 2
  );
  const hasEnoughResumeContent = Boolean(
    hasResumeStructure ||
    (hasIdentity && (hasSummary || hasSkills || hasWorkExperience || hasEducation))
  );

  // Only mark as missing if truly missing or invalid
  if (!hasFullName) {
    missingFields.push('fullName');
  } else {
    const nameValidation = validateName(normalizedData.fullName!, 'Full name');
    if (!nameValidation.isValid) {
      errors.push(nameValidation.error!);
    }
  }

  // Uploaded resumes can contain contact details in headers/images that parsers
  // miss. Treat email/phone as review-time fields, not hard blockers, when the
  // resume structure itself is clear.
  if (!hasEmail && !hasEnoughResumeContent) {
    missingFields.push('email');
  } else if (hasEmail) {
    const emailValidation = validateEmail(normalizedData.email!);
    if (!emailValidation.isValid) {
      errors.push(emailValidation.error!);
    }
  }

  if (!hasPhone && !hasEnoughResumeContent) {
    missingFields.push('phone');
  } else if (hasPhone) {
    const phoneValidation = validatePhone(normalizedData.phone!);
    if (!phoneValidation.isValid) {
      errors.push(phoneValidation.error!);
    }
  }

  if (!hasWorkExperience && !hasSkills && !hasEnoughResumeContent) {
    missingFields.push('workExperience');
  }

  if (!hasEducation && !hasWorkExperience && !hasEnoughResumeContent) {
    missingFields.push('education');
  }

  return {
    isValid: missingFields.length === 0 && errors.length === 0,
    missingFields,
    errors,
    hasWorkExperience,
    hasEducation
  };
};
